import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Select, Spin, Badge, Tag } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { ElasticsearchService } from '@client/services/elasticsearchService';
import { HtmlContent } from '@client/utils/html_content';
import DownIcon from '../../../../img/ant-down-icon.svg';
import { useTranslation } from 'react-i18next';

/**
 * @typedef {Object} ElasticsearchOption
 * @property {string} label - Display label
 * @property {string} value - Option value
 * @property {number} [count] - Result count
 * @property {boolean} [highlighted] - Highlight status
 */

/**
 * @typedef {Object} ElasticsearchSelectProps
 * @property {string} document - Document type for Elasticsearch
 * @property {string} field - Field to search in
 * @property {string} [label] - Label translation key
 * @property {number} [debounceTimeout] - Search debounce in ms
 * @property {number} [pageSize] - Results per page
 * @property {string} [sortBy] - Sort field
 * @property {string} [sortOrder] - Sort direction
 * @property {boolean} [showCount] - Display result counts
 * @property {boolean} [infinite] - Enable infinite scroll
 * @property {any} value - Selected value(s)
 * @property {Function} onChange - Change handler
 * @property {boolean} [showSelectedTags] - Show selected items as tags
 * @property {string} [value_field] - Value field mapping
 * @property {string} [label_field] - Label field mapping
 * @property {Object} [languageFields] - Language field mappings
 * @property {Object} [languageLabels] - Language display labels
 * @property {string} [defaultLanguage] - Default language code
 * @property {boolean} [showLanguageSelector] - Show language switcher
 * @property {Function} [labelTransform] - Function to transform option labels (label, value) => transformedLabel
 */

const CONFIG = {
  DEBOUNCE_TIMEOUT: 150,
  PAGE_SIZE: 20,
  SORT_BY: 'count',
  SORT_ORDER: 'desc',
  SCROLL_THRESHOLD: 0,
  STORAGE_PREFIX: 'lang_',
};

const TRANSLATIONS = {
  LOADING: 'Chargement...',
  NO_RESULTS: 'Aucun résultat trouvé',
  SEARCH: 'Rechercher...',
  SELECT: 'Sélectionner',
};

/**
 * Strips HTML tags from string
 * @param {string} html - HTML string
 * @returns {string} Plain text
 */
const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

/**
 * Option renderer component
 * @param {Object} props
 * @param {ElasticsearchOption} props.option - Option data
 * @param {boolean} props.showCount - Show count badge
 * @returns {JSX.Element}
 */
const OptionRenderer = ({ option, showCount }) => (
  <div className="elasticsearch-option">
    <span
      className={`option-label ${option.data?.highlighted ? 'highlighted' : ''}`}
    >
      <HtmlContent html={option.label} />
    </span>
    {showCount && option.data?.count && (
      <Badge count={option.data.count} className="option-count" />
    )}
  </div>
);

/**
 * Selected value tags component
 * @param {Object} props
 * @param {Array} props.value - Selected values
 * @param {Array} props.options - Available options
 * @param {Function} props.onRemove - Remove handler
 * @returns {JSX.Element|null}
 */
const SelectedValueTags = ({ value, options, onRemove }) => {
  if (!Array.isArray(value) || value.length === 0) return null;

  const tags = value.map((selectedValue) => {
    const option = options.find((opt) => opt.value === selectedValue);
    const displayLabel = option ? stripHtml(option.label) : selectedValue;

    return (
      <Tag
        key={selectedValue}
        closable
        onClose={() => onRemove(selectedValue)}
        className="selected-tag"
      >
        {displayLabel}
      </Tag>
    );
  });

  return <div className="selected-tags-container">{tags}</div>;
};

/**
 * Language selector component
 * @param {Object} props
 * @param {string} props.selectedLanguage - Current language
 * @param {Object} props.languageLabels - Language options
 * @param {Function} props.onChange - Change handler
 * @returns {JSX.Element}
 */
const LanguageSelector = ({ selectedLanguage, languageLabels, onChange }) => (
  <Select
    size="small"
    value={selectedLanguage}
    onChange={onChange}
    options={Object.entries(languageLabels).map(([code, label]) => ({
      value: code,
      label: label,
    }))}
    className="language-selector"
    suffixIcon={<GlobalOutlined />}
  />
);

/**
 * Elasticsearch-powered select component with infinite scroll
 * @component
 * @param document
 * @param label
 * @param field
 * @param debounceTimeout
 * @param pageSize
 * @param sortBy
 * @param sortOrder
 * @param showCount
 * @param infinite
 * @param value
 * @param onChange
 * @param showSelectedTags
 * @param value_field
 * @param label_field
 * @param languageFields
 * @param languageLabels
 * @param defaultLanguage
 * @param showLanguageSelector
 * @param {ElasticsearchSelectProps} props
 * @returns {JSX.Element}
 */
const ElasticsearchSelect = React.forwardRef(({
  document,
  label = 'common|Select',
  field,
  debounceTimeout = CONFIG.DEBOUNCE_TIMEOUT,
  pageSize = CONFIG.PAGE_SIZE,
  sortBy = CONFIG.SORT_BY,
  sortOrder = CONFIG.SORT_ORDER,
  showCount = true,
  infinite = true,
  value,
  onChange,
  showSelectedTags = true,
  value_field = null,
  label_field = null,
  languageFields = null,
  languageLabels = null,
  defaultLanguage = 'fr',
  showLanguageSelector = false,
  labelTransform = null,
  ...props
}, ref) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [options, setOptions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    if (!languageFields) return null;
    const saved = localStorage.getItem(`${CONFIG.STORAGE_PREFIX}${field}`);
    return saved || defaultLanguage;
  });

  const currentLabelField = useMemo(() => {
    return languageFields?.[selectedLanguage] || label_field;
  }, [languageFields, selectedLanguage, label_field]);

  /**
   * Fetches search results from Elasticsearch
   * @param {string} query - Search query
   * @param {number} page - Page number
   * @param {boolean} append - Append to existing results
   */
  const fetchResults = useCallback(
    async (query, page, append = false) => {
      if (!document || !field) return;

      const isNewSearch = !append;
      setLoading(!isNewSearch);
      setSearching(isNewSearch);

      try {
        const response = await ElasticsearchService.search({
          document,
          field,
          q: query || '',
          page,
          size: pageSize,
          sort: sortBy,
          order: sortOrder,
          value_field,
          label_field: currentLabelField,
        });

        const formattedOptions = response.options.map((option) => ({
          label: labelTransform ? labelTransform(option.label, option.value) : option.label,
          value: option.value,
          data: {
            count: option.count,
            highlighted: option.highlighted,
          },
        }));

        setOptions((prev) =>
          append ? [...prev, ...formattedOptions] : formattedOptions
        );
        setHasMore(response.pagination.has_more);
        setCurrentPage(page);
      } catch (error) {
        console.error('ElasticsearchSelect fetch error:', error);
        if (!append) setOptions([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        setSearching(false);
      }
    },
    [
      document,
      field,
      pageSize,
      sortBy,
      sortOrder,
      value_field,
      currentLabelField,
      labelTransform,
    ]
  );

  /**
   * Handles scroll event for infinite loading
   * @param {Event} event - Scroll event
   */
  const handleScroll = useCallback(
    async (event) => {
      if (!infinite || !hasMore || loading) {
        return;
      }

      const { target } = event;
      const atBottom =
        target.scrollTop + target.offsetHeight >= target.scrollHeight - 5;

      if (atBottom) {
        const nextPage = currentPage + 1;
        await fetchResults(searchQuery, nextPage, true);
      }
    },
    [infinite, hasMore, loading, currentPage, searchQuery, fetchResults]
  );

  /**
   * Debounced search handler
   */
  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        setSearchQuery(query);
        setCurrentPage(1);
        fetchResults(query, 1, false);
      }, debounceTimeout),
    [fetchResults, debounceTimeout]
  );

  /**
   * Handles language change
   * @param {string} lang - Language code
   */
  const handleLanguageChange = useCallback(
    (lang) => {
      setSelectedLanguage(lang);
      localStorage.setItem(`${CONFIG.STORAGE_PREFIX}${field}`, lang);
      setOptions([]);
      setCurrentPage(1);
      setSearchQuery('');
      setHasMore(true);
    },
    [field]
  );

  /**
   * Handles dropdown visibility change
   * @param {boolean} open - Visibility state
   */
  const handleDropdownVisibleChange = useCallback(
    (open) => {
      if (open && options.length === 0 && !loading) {
        fetchResults('', 1, false);
      } else if (!open && searchQuery) {
        // Reset search when dropdown closes to avoid confusion
        setSearchQuery('');
        setCurrentPage(1);
        fetchResults('', 1, false);
      }
    },
    [options.length, loading, fetchResults, searchQuery]
  );

  /**
   * Handles tag removal
   * @param {string} removedValue - Value to remove
   */
  const handleRemoveTag = useCallback(
    (removedValue) => {
      const newValue = Array.isArray(value)
        ? value.filter((v) => v !== removedValue)
        : undefined;
      onChange?.(newValue);
    },
    [value, onChange]
  );

  /**
   * Handles value change
   * @param {any} newValue - New value(s)
   */
  const handleChange = useCallback(
    (newValue) => {
      const selectedItems = Array.isArray(newValue)
        ? newValue.map((val) => {
            const option = options.find((opt) => opt.value === val);
            return option
              ? { value: val, label: option.label }
              : { value: val, label: val };
          })
        : newValue;

      onChange(newValue, selectedItems);
    },
    [options, onChange]
  );

  useEffect(() => {
    if (languageFields && selectedLanguage) {
      setOptions([]);
      setCurrentPage(1);
      setSearchQuery('');
      setHasMore(true);
      fetchResults('', 1);
    }
  }, [currentLabelField]);

  useEffect(() => {
    if (value_field && label_field && Array.isArray(value) && value.length > 0 && options.length === 0) {
      fetchResults('', 1, false);
    }
  }, []);

  useEffect(() => {
    if (!languageFields) return;

    const handleExternalLanguageChange = (event) => {
      handleLanguageChange(event.detail);
    };

    const eventName = `languageChanged_${field}`;
    window.addEventListener(eventName, handleExternalLanguageChange);

    return () => {
      window.removeEventListener(eventName, handleExternalLanguageChange);
    };
  }, [field, languageFields, handleLanguageChange]);

  const selectOptions = useMemo(() => {
    const processedOptions = options.map((option) => ({
      ...option,
      label: option.label,
      value: option.value,
    }));

    if (loading && infinite) {
      processedOptions.push({
        value: '__loading__',
        label: TRANSLATIONS.LOADING,
        disabled: true,
      });
    }

    return processedOptions;
  }, [options, loading, infinite]);

  return (
    <div className="elasticsearch-select-container">
      <div className="select-header">
        <label className="select-label">{t(label)}</label>
        {showLanguageSelector && languageFields && languageLabels && (
          <LanguageSelector
            selectedLanguage={selectedLanguage}
            languageLabels={languageLabels}
            onChange={handleLanguageChange}
          />
        )}
      </div>

      <Select
        ref={ref}
        key={`${field}-${selectedLanguage}`}
        className="elasticsearch-select"
        labelInValue={false}
        filterOption={false}
        onSearch={debouncedSearch}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        onPopupScroll={infinite ? handleScroll : undefined}
        placeholder={t('common|Search...') || TRANSLATIONS.SEARCH}
        notFoundContent={
          searching ? (
            <Spin size="small" />
          ) : (
            t('common|No results found') || TRANSLATIONS.NO_RESULTS
          )
        }
        loading={searching}
        value={value}
        onChange={handleChange}
        showSearch
        autoClearSearchValue
        options={selectOptions}
        optionRender={(option) => (
          <OptionRenderer option={option} showCount={showCount} />
        )}
        suffixIcon={<img src={DownIcon} alt="Down" className="down-icon" />}
        {...props}
      />

      {showSelectedTags && (
        <SelectedValueTags
          value={value}
          options={options}
          onRemove={handleRemoveTag}
        />
      )}
    </div>
  );
});

ElasticsearchSelect.displayName = 'ElasticsearchSelect';

export default React.memo(ElasticsearchSelect);
